import { Component, EventEmitter, inject, Input, OnInit, Output, signal, ViewChild } from '@angular/core';
import { generateId } from '../../../../_core/utils/generate-id';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TextareaComponent } from '../../../../_shared/components/forms/textarea/textarea.component';
import { requiredWithUsername } from '../../../validators/comments/required-with-username.validator';
import { ConfigService } from '../../../../services/config.service';
import { UserFacade } from '../../../../_core/state/user/user.facade';
import { toSignal } from '@angular/core/rxjs-interop';

export type SubmitReplyEvent = {
  comment: string,
  username: string | null,
}

@Component({
  selector: 'app-edit-comment',
  templateUrl: './edit-comment.component.html',
  styleUrls: ['./edit-comment.component.scss']
})
export class EditCommentComponent implements OnInit {
  private readonly configService = inject(ConfigService);
  private readonly userFacade = inject(UserFacade);

  @ViewChild('textarea', { static: true }) textarea: TextareaComponent;

  @Input() submitLabel: string = 'comments.actions.reply';
  @Input() textareaId: string;
  @Input() username: string | null = null;

  @Output() submitReply = new EventEmitter<SubmitReplyEvent>();
  @Output() cancelReply = new EventEmitter<void>();

  readonly formId = signal<string>(`comment-form-${generateId()}`);
  readonly maxLength = signal<number>(this.configService.get().maxReplyCommentLength);
  readonly userPicture = toSignal(this.userFacade.profilePicture$);
  form: FormGroup;

  get commentControl() {
    return this.form.controls['comment'];
  }

  ngOnInit() {
    const initialValue = this.username ? this.username + ' ' : '';

    this.form = new FormGroup({
      comment: new FormControl(initialValue, [requiredWithUsername(this.username), Validators.maxLength(this.maxLength())]),
    });

    this.textarea.focus();
  }

  reply(event: Event) {
    event.preventDefault();

    let hasUsername = false;
    let comment = this.form.getRawValue().comment.trim();

    if (this.username !== null) {
      hasUsername = comment.startsWith(this.username);

      if (hasUsername) {
        comment = comment.substring(this.username.length).trim();
      }
    }

    if (this.commentControl.invalid) {
      this.form.controls['comment'].markAsTouched();
      this.textarea.focus();
      return;
    }

    this.submitReply.emit({
      comment,
      username: hasUsername ? this.username : null,
    });

    this.form.reset();
  }

  cancel() {
    this.cancelReply.emit();
  }
}
